import type { DistinctPage, DistinctQuery, RowPage, RowQuery, RowStamp, SqlRunResult, TableStamp, WorkspaceContents } from '@easydb/shared';
import type { EasydbStoreBridge } from '../data-store-bridge.js';
import type { EdbCall, EdbRequest, EdbResponse, PeekedWorkspace } from './protocol.js';

/**
 * The main-thread half of the worker bridge.
 *
 * Implements `EasydbStoreBridge` — the same interface Electron's preload
 * satisfies — so `createIpcDataStore` builds the whole `DataStore` on top of it
 * with no change. The windowed row view, the row-fetch cap and the change
 * plumbing all come along for free.
 */

export interface EdbBridge extends EasydbStoreBridge {
  /** Told when the worker hits a non-fatal problem worth showing the user. */
  onWarning(cb: (message: string) => void): () => void;
  /** The database as bytes, for Save to write to the user's file. */
  export(): Promise<Uint8Array>;
  /**
   * Replace the contents — a fresh workspace, or a file the user just opened.
   *
   * **Throwaway by default.** Without `pooled` this worker stays off the
   * `opfs-sahpool` VFS and writes no mirror, which is what a copy nobody will
   * keep wants: the pool is exclusive origin-wide, and a second worker asking
   * for it makes the browser refuse the live session's own access handles.
   *
   * `pooled: true` is for the ONE worker that owns this tab's database
   * (`session.ts`). Anything else passing it breaks the session running beside
   * it — see `protocol.ts` for why the default is this way round.
   */
  open(bytes: Uint8Array | null, name: string, opts?: { pooled?: boolean }): Promise<void>;
  /**
   * Put a database under `name` where the next boot will find it, without
   * switching to it.
   *
   * Open and Convert both end in a reload, and the bytes have to be in place
   * first. Only this worker can put them there: the `opfs-sahpool` VFS is
   * exclusive origin-wide, so the throwaway worker that BUILT them never got the
   * pool and wrote its copy where no boot looks.
   *
   * `adopt: true` switches to it as well — for the caller that has just made
   * this file the tab's own with no reload to follow (`saveIntoFolder`).
   */
  importBytes(name: string, bytes: Uint8Array, opts?: { adopt?: boolean }): Promise<void>;
  /**
   * The OPFS mirror's bytes for a workspace, or null.
   *
   * What a reload uses: the mirror needs no file permission at all, so the
   * workspace comes back whatever state the `FileSystemFileHandle` is in.
   */
  restore(name: string): Promise<Uint8Array | null>;
  /**
   * Write the OPFS mirror now.
   *
   * Call this before a reload that is meant to land on these bytes. Open and
   * Convert both do, because the boot reads the mirror and nothing else.
   */
  flush(): Promise<void>;
  /**
   * Does this browser already hold a database of that name?
   *
   * `?space=NAME` resolution asks before adopting anything, so a link can reach a
   * workspace this browser has but the OPEN database has never heard of.
   */
  hasDatabase(name: string): Promise<boolean>;
  /**
   * Move a database from one name to another, opening neither.
   *
   * One caller: the boot that renames this browser's own database from `local.edb`
   * to `index.edp` (see `session.ts`). False means there was nothing to move,
   * which is every boot after the first.
   */
  renameDatabase(from: string, to: string): Promise<boolean>;
  /**
   * The workspace records inside a `.edb`'s bytes, without adopting the file.
   *
   * A folder scan calls this once per file. Nothing is imported and the live
   * session is untouched — see the worker's own note.
   *
   * Each record arrives with its table and view counts, which is what lets a
   * prompt say how the copy in a file differs from the copy in this browser.
   */
  peekWorkspaces(bytes: Uint8Array): Promise<PeekedWorkspace[]>;
  /**
   * The same, for a database this browser already holds, by NAME.
   *
   * What the other side of a conflict prompt is: the file's counts come free
   * with the folder scan, and this is how the browser's own copy can be counted
   * without adopting it or copying it out. Empty for a name nothing holds.
   */
  peekDatabase(name: string): Promise<PeekedWorkspace[]>;
  /**
   * One stamp per table of a workspace, for comparing this database with another
   * copy of it. Cheap by design — two aggregates per table, no rows.
   */
  tableStamps(workspaceId: string): Promise<TableStamp[]>;
  /** Every row of one table as an id and a timestamp. */
  rowStamps(tableId: string): Promise<RowStamp[]>;
  terminate(): void;
}

export function createEdbBridge(): EdbBridge {
  const worker = new Worker(new URL('./worker.js', import.meta.url), { type: 'module' });
  const pending = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void }>();
  const listeners = new Set<(coll: string, scope?: string) => void>();
  const warners = new Set<(message: string) => void>();
  let nextId = 1;

  worker.onmessage = (e: MessageEvent<EdbResponse>) => {
    const msg = e.data;
    if ('warning' in msg) {
      for (const fn of warners) fn(msg.warning);
      return;
    }
    if ('changed' in msg) {
      for (const fn of listeners) fn(msg.changed, msg.scope);
      return;
    }
    const slot = pending.get(msg.id);
    if (!slot) return;
    pending.delete(msg.id);
    if (msg.ok) slot.resolve(msg.result);
    else slot.reject(new Error(msg.error));
  };

  // A worker that dies takes every in-flight call with it. Rejecting them beats
  // leaving the UI waiting on promises that can never settle.
  worker.onerror = (e) => {
    const err = new Error(`edb worker failed: ${e.message}`);
    for (const [, slot] of pending) slot.reject(err);
    pending.clear();
  };

  function call<T>(req: EdbCall): Promise<T> {
    const id = nextId++;
    return new Promise<T>((resolve, reject) => {
      pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      worker.postMessage({ ...req, id } as EdbRequest);
    });
  }

  return {
    open: (bytes, name, opts) => call<void>({ op: 'open', bytes, name, pooled: opts?.pooled }),
    restore: (name) => call<Uint8Array | null>({ op: 'restore', name }),
    importBytes: (name, bytes, opts) => call<void>({ op: 'importBytes', name, bytes, adopt: opts?.adopt }),
    flush: () => call<void>({ op: 'flush' }),
    hasDatabase: (name) => call<boolean>({ op: 'hasDatabase', name }),
    renameDatabase: (from, to) => call<boolean>({ op: 'renameDatabase', from, to }),
    peekWorkspaces: (bytes) => call<PeekedWorkspace[]>({ op: 'peekWorkspaces', bytes }),
    peekDatabase: (name) => call<PeekedWorkspace[]>({ op: 'peekDatabase', name }),
    tableStamps: (workspaceId) => call<TableStamp[]>({ op: 'tableStamps', workspaceId }),
    rowStamps: (tableId) => call<RowStamp[]>({ op: 'rowStamps', tableId }),
    export: () => call<Uint8Array>({ op: 'export' }),
    find: (coll, query, limit) => call<unknown[]>({ op: 'find', coll, query, limit }),
    findOne: (coll, key) => call<unknown | null>({ op: 'findOne', coll, key }),
    insert: (coll, doc) => call<unknown>({ op: 'insert', coll, doc }),
    bulkInsert: (coll, docs) => call<unknown[]>({ op: 'bulkInsert', coll, docs }),
    bulkUpdate: (coll, docs) => call<void>({ op: 'bulkUpdate', coll, docs }),
    upsert: (coll, doc) => call<unknown>({ op: 'upsert', coll, doc }),
    patch: (coll, key, patch) => call<unknown>({ op: 'patch', coll, key, patch }),
    remove: (coll, key) => call<void>({ op: 'remove', coll, key }),
    bulkRemove: (coll, keys) => call<void>({ op: 'bulkRemove', coll, keys }),
    count: (coll) => call<number>({ op: 'count', coll }),
    countRows: (tableId) => call<number>({ op: 'countRows', tableId }),
    queryRows: (tableId, q: RowQuery) => call<RowPage>({ op: 'queryRows', tableId, query: q }),
    // Feature-detected by the caller, so declaring it here is what turns a
    // funnel's value list from a client-side scan into a SQL GROUP BY.
    distinctValues: (tableId, q: DistinctQuery) => call<DistinctPage>({ op: 'distinctValues', tableId, query: q }),
    // Also feature-detected: its presence is what tells the chrome this
    // workspace is a real database it can offer a SQL console for.
    runSql: (sql, opts) => call<SqlRunResult>({ op: 'runSql', sql, params: opts?.params, write: opts?.write, maxRows: opts?.maxRows }),
    countWorkspaceContents: (workspaceId, opts) => call<WorkspaceContents>({ op: 'countWorkspaceContents', workspaceId, countRows: opts?.countRows }),
    deleteWorkspace: (workspaceId) => call<WorkspaceContents>({ op: 'deleteWorkspace', workspaceId }),
    cloneWorkspace: (opts) => call<string>({ op: 'cloneWorkspace', ...opts }),
    dbPath: () => call<string>({ op: 'dbName' }),
    onWarning(cb) {
      warners.add(cb);
      return () => warners.delete(cb);
    },
    onChanged(cb) {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    terminate() {
      worker.terminate();
      pending.clear();
      listeners.clear();
      warners.clear();
    },
  };
}
